import { Sparkles, RefreshCw, Crown } from 'lucide-react';
import { useProfile } from '../../../../../context/profile/ProfileContext';

const SubscriptionChangePlanCard = () => {
  const {
    subscription,
    isTrialUser,
    allPlans,
    setShowCycModal,
    handleUpgradeClick,
    formatCurrency
  } = useProfile();

  const currentPlan = subscription?.plan_id;
  const planName = currentPlan?.name || 'Pro';
  const monthlyPrice = currentPlan?.monthly_price || (currentPlan?.slug === 'growth' ? 3499 : currentPlan?.slug === 'starter' ? 699 : 1499);
  const annualPrice = currentPlan?.yearly_price || Math.round(monthlyPrice * 12 * 0.80);
  const annualMonthlyEquivalent = currentPlan?.annual_monthly_equivalent || Math.round(annualPrice / 12);
  const annualSavings = (monthlyPrice * 12) - annualPrice;
  const isAlreadyAnnual = subscription?.billing_interval === 'year';

  const slugRanks = { starter: 1, pro: 2, growth: 3, enterprise: 4 };
  const currentSlug = (currentPlan?.slug || 'pro').toLowerCase();
  const currentRank = slugRanks[currentSlug] || 2;

  const defaultTierPlans = [
    { _id: 'starter_default', name: 'Starter', slug: 'starter', monthly_price: 699, vouchers_per_month: 'Unlimited', rank: 1, desc: 'Basic traveller app & tracking.' },
    { _id: 'pro_default', name: 'Pro', slug: 'pro', monthly_price: 1499, vouchers_per_month: 'Unlimited', rank: 2, desc: 'Commission wallet, flight tracking & live alerts.' },
    { _id: 'growth_default', name: 'Growth', slug: 'growth', monthly_price: 3499, vouchers_per_month: 'Unlimited', rank: 3, desc: '+20% commission booster, own vendors & custom design.' }
  ];

  const planList = allPlans && allPlans.length > 0 ? allPlans : defaultTierPlans;
  const upgradePlans = isTrialUser
    ? []
    : planList.filter(p => {
        const pSlug = (p.slug || '').toLowerCase();
        const pRank = slugRanks[pSlug] || p.display_order || 1;
        return pRank > currentRank && pSlug !== 'enterprise';
      });

  return (
    <section className="bg-white rounded-2xl sm:rounded-3xl border border-gray-100 p-4 sm:p-6 lg:p-7 shadow-sm">
      <div className="flex justify-between items-center border-b border-slate-100 pb-3.5 sm:pb-4 mb-3.5 sm:mb-4">
        <div>
          <h2 className="font-semibold text-slate-900 text-sm sm:text-base">Change plan</h2>
          <div className="text-xs text-slate-500 mt-0.5 font-normal">
            Upgrades apply immediately; downgrades &amp; cycle switches are coordinated with our team.
          </div>
        </div>
      </div>
      <div className="space-y-3">
        {isTrialUser ? (
          (() => {
            const proPlan = planList.find(p => (p.slug || '').toLowerCase() === 'pro') || defaultTierPlans.find(p => p.slug === 'pro');
            const proMonthlyPrice = proPlan?.monthly_price || 1499;

            return (
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-3.5 sm:items-center p-3.5 sm:p-4 border border-slate-100 rounded-2xl hover:bg-slate-50/50">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 bg-teal-50 text-[#067978] rounded-xl flex items-center justify-center shrink-0">
                    <Sparkles size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <b className="block text-sm font-semibold text-slate-800">Pro monthly plan</b>
                    <span className="text-xs text-slate-500 block leading-tight">
                      {formatCurrency(proMonthlyPrice)}/month + GST. Flight tracking, live alerts &amp; full app features.
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between sm:justify-end gap-2.5 shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                  <span className="text-[11px] font-semibold text-[#067978] bg-teal-50 px-2.5 py-1 rounded-full whitespace-nowrap">
                    Recommended
                  </span>
                  <button
                    onClick={() => handleUpgradeClick(proPlan)}
                    className="px-3.5 py-1.5 bg-[#067978] hover:bg-[#05605f] text-white text-xs font-bold rounded-xl shadow-md shadow-[#067978]/20 transition-all flex items-center gap-1.5 active:scale-95 cursor-pointer whitespace-nowrap"
                  >
                    <Sparkles size={12} />
                    <span>Subscribe</span>
                  </button>
                </div>
              </div>
            );
          })()
        ) : (
          <>
            {upgradePlans.map(p => (
              <div key={p._id || p.slug} className="flex flex-col sm:flex-row gap-3 sm:gap-3.5 sm:items-center p-3.5 sm:p-4 border border-slate-100 rounded-2xl hover:bg-slate-50/50">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 bg-teal-50 text-[#067978] rounded-xl flex items-center justify-center shrink-0">
                    <Sparkles size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <b className="block text-sm font-semibold text-slate-800">Upgrade to {p.name}</b>
                    <span className="text-xs text-slate-500 block leading-tight">
                      {p.description || p.desc || '+20% commission boost.'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between sm:justify-end gap-2.5 shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                  <span className="text-[11px] font-semibold text-[#067978] bg-teal-50 px-2.5 py-1 rounded-full whitespace-nowrap">
                    Starts today · instant
                  </span>
                  <button
                    onClick={() => handleUpgradeClick(p)}
                    className="px-3.5 py-1.5 bg-[#067978] hover:bg-[#05605f] text-white text-xs font-bold rounded-xl shadow-md shadow-[#067978]/20 transition-all flex items-center gap-1.5 active:scale-95 cursor-pointer whitespace-nowrap"
                  >
                    <Sparkles size={12} />
                    <span>Upgrade</span>
                  </button>
                </div>
              </div>
            ))}

            {/* Annual Switch Option (If currently Monthly) */}
            {!isAlreadyAnnual && (
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-3.5 sm:items-center p-3.5 sm:p-4 border border-slate-100 rounded-2xl hover:bg-slate-50/50">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 bg-teal-50 text-[#067978] rounded-xl flex items-center justify-center shrink-0">
                    <RefreshCw size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <b className="block text-sm font-semibold text-slate-800">Switch to annual billing</b>
                    <span className="text-xs text-slate-500 block leading-tight">
                      Same {planName} plan at <b className="font-semibold text-slate-700">{formatCurrency(annualMonthlyEquivalent)}/month</b> (billed annually at {formatCurrency(annualPrice)}/yr + GST). Save {formatCurrency(annualSavings)}/year.
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between sm:justify-end gap-2.5 shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                  <span className="text-[11px] font-semibold text-[#067978] bg-teal-50 px-2.5 py-1 rounded-full whitespace-nowrap">
                    Save 20% · instant
                  </span>
                  <button
                    onClick={() => setShowCycModal(true)}
                    className="px-3.5 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-xl font-semibold text-xs border border-slate-200 hover:border-slate-300 transition-all flex items-center gap-1.5 shadow-2xs active:scale-95 cursor-pointer whitespace-nowrap"
                  >
                    <RefreshCw size={12} className="text-slate-500" />
                    <span>Switch</span>
                  </button>
                </div>
              </div>
            )}

            {/* If on highest tier and already annual */}
            {upgradePlans.length === 0 && isAlreadyAnnual && (
              <div className="flex gap-3.5 items-center p-4 bg-teal-50/40 border border-teal-100 rounded-2xl">
                <div className="w-10 h-10 bg-teal-100 text-[#067978] rounded-xl flex items-center justify-center shrink-0">
                  <Crown size={18} />
                </div>
                <div className="flex-1">
                  <b className="block text-sm font-semibold text-slate-900">Highest Tier Active</b>
                  <span className="text-xs text-slate-500">
                    You are on the {planName} tier with full access to all features.
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default SubscriptionChangePlanCard;
